export type AnimalSexType = 'm' | 'bull' | 'steer' | 'f' | 'heifer' | 'cow';

export function calculateYieldHelper(
  liveWeight: number,
  hangingWeight: number,
) {
  const yieldValue = liveWeight === 0 ? 0 : hangingWeight / liveWeight;

  return `${(yieldValue * 100).toFixed(2)}%`;
}

export interface AnimalHeadHangingWeightData {
  hangingWeight: number;
  isHangingWeightPerSide?: boolean;
  hangingWeightSideA?: number;
  hangingWeightSideB?: number;
}

/**
 * Calculates the hanging weight for a specific split based on the animal head's
 * hanging weight data, split type, split part, and split index.
 * @param animalHead - The animal head containing hanging weight data
 * @param splitType - The split type of the entire animal (e.g., 'whole', 'half', 'quarters', 'half_and_two_quarters')
 * @param splitPart - The part type of this specific split (e.g., 'whole', 'half', 'quarter')
 * @param splitIndex - The index of this split within the animal head's cutsheet information array
 * @returns The calculated hanging weight for this split
 *
 * @example
 * // For a half split with per-side weights
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'half',
 *   'half',
 *   0
 * ) // Returns 310 (SideA)
 *
 * @example
 * // For quarters with per-side weights
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'quarters',
 *   'quarter',
 *   2
 * ) // Returns 145 (half of SideB, since index 2 is in the second pair)
 *
 * @example
 * // For half_and_two_quarters where index 0 is half, indexes 1-2 are quarters
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'half_and_two_quarters',
 *   'half',
 *   0
 * ) // Returns 310 (first half is SideA)
 */
export function calculateSplitHangingWeight(
  animalHead: AnimalHeadHangingWeightData,
  splitType: string,
  splitPart: string,
  splitIndex: number,
): number {
  const isPerSide = animalHead.isHangingWeightPerSide ?? false;
  const sideAWeight = animalHead.hangingWeightSideA ?? 0;
  const sideBWeight = animalHead.hangingWeightSideB ?? 0;

  const baseHangingWeight = isPerSide
    ? (sideAWeight || 0) + (sideBWeight || 0)
    : (animalHead.hangingWeight ?? 0);

  const lowerSplitPart = splitPart.toLowerCase();

  // Handle 'whole' split part
  if (lowerSplitPart === 'whole') {
    return baseHangingWeight;
  }

  // Handle 'half' split part
  if (lowerSplitPart.startsWith('half')) {
    if (isPerSide) {
      // For half_and_two_quarters: index 0 is the first half (SideA)
      // For regular half: index 0 = SideA, index 1 = SideB
      return splitIndex === 0 ? sideAWeight : sideBWeight;
    }
    return baseHangingWeight * 0.5;
  }

  // Handle 'quarter' split part
  if (lowerSplitPart.startsWith('quarter')) {
    if (isPerSide) {
      if (splitType === 'half_and_two_quarters') {
        // For half_and_two_quarters:
        // index 0 = half (handled above)
        // index 1 & 2 = quarters from the second half (SideB)
        // Each quarter is half of SideB
        return sideBWeight * 0.5;
      }

      // For regular quarters:
      // Quarters 0-1 are from SideA, Quarters 2-3 are from SideB
      // Each quarter is half of its respective side
      const sideWeight = splitIndex < 2 ? sideAWeight : sideBWeight;
      return sideWeight * 0.5;
    }

    return baseHangingWeight * 0.25;
  }

  // Fallback: return full hanging weight for unknown split parts
  return baseHangingWeight;
}

export const getSplitMultiplier = (splitPart: string) => {
  const lowerSplitPart = splitPart.toLowerCase();

  if (lowerSplitPart.startsWith('half')) return 0.5;

  if (lowerSplitPart.startsWith('quarter')) return 0.25;

  return 1;
};
